import type { BasicReview, ReviewSearchType } from '@shared/domain/review';
import type { ComponentType } from 'react';
import { RateReview, Sort } from '@mui/icons-material';
import { Alert, Box, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { Page } from '@shared/components/Page';
import StaticTextCard from '@shared/components/StaticTextCard';
import { ReviewStatus } from '@shared/domain/review';
import { useApi } from '@shared/services/api';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@/shared/contexts/UserContext';
import { log } from '../utils/log';

interface ReviewCardProps {
  review: BasicReview;
  index: number;
  onGoToReview?: () => void;
  onGotoCompany: () => void;
  userId: number | undefined;
  filterType: ReviewSearchType;
  showTypeIndicator?: boolean;
}

interface AdminReviewFeedPageProps {
  ReviewCardComponent: ComponentType<ReviewCardProps>;
}

type SortOption = 'status' | 'createdAt' | 'companyName';

function AdminReviewFeedPage({ ReviewCardComponent }: AdminReviewFeedPageProps) {
  const { user } = useContext(UserContext);
  const api = useApi();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState<BasicReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('status');

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('searchType', 'custom');
      params.append('limit', '200');
      params.append('orderBy', 'createdAt');
      params.append('orderDirection', 'DESC');

      const response = await api.get(`/review?${params.toString()}`);
      if (response.success) {
        setReviews(response.data || []);
      }
    } catch (error) {
      log.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (user?.isAdmin && user?.idToken) {
      fetchReviews();
    }
  }, [user?.isAdmin, user?.idToken]);

  const handleGoToReview = (review: BasicReview): void => {
    if (review.companyName && review.id) {
      navigate(`/review-reader/${encodeURIComponent(review.companyName)}/${review.id}`);
    }
  };

  if (!user) {
    return <LoadingSpinner variant="linear" size="medium" />;
  }

  if (!user.isAdmin) {
    return <StaticTextCard header="Unauthorized" text="You are not authorized to view this page." link={false} />;
  }

  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortBy === 'status') {
      const statusOrder: Record<string, number> = {
        [ReviewStatus.PENDING_APPROVAL]: 0,
        [ReviewStatus.NEEDS_EDITING]: 1,
        [ReviewStatus.IN_PROGRESS]: 2,
        [ReviewStatus.COMPLETED]: 3,
        [ReviewStatus.REJECTED]: 4,
        [ReviewStatus.ARCHIVED]: 5,
        [ReviewStatus.DELETED]: 6,
      };
      const aOrder = statusOrder[a.status] ?? 99;
      const bOrder = statusOrder[b.status] ?? 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    }
    if (sortBy === 'companyName') {
      return (a.companyName || '').localeCompare(b.companyName || '');
    }
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });

  const pendingCount = reviews.filter((r) => r.status === ReviewStatus.PENDING_APPROVAL).length;

  return (
    <Page title="Admin Review Feed" description="Review and approve submitted reviews" icon={RateReview}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {pendingCount > 0 && (
          <Chip label={`${pendingCount} Pending Approval`} color="warning" size="small" sx={{ fontWeight: 600 }} />
        )}
        <Chip label={`${reviews.length} Total`} variant="outlined" size="small" />
        <FormControl size="small" sx={{ minWidth: 180, ml: 'auto' }}>
          <InputLabel>
            <Sort sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
            Sort By
          </InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            data-testid="admin-review-feed-sort"
          >
            <MenuItem value="status">Status (Pending First)</MenuItem>
            <MenuItem value="createdAt">Date (Newest First)</MenuItem>
            <MenuItem value="companyName">Company Name</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <LoadingSpinner variant="circular" size="large" message="Loading reviews..." fullScreen />
      ) : sortedReviews.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sortedReviews.map((review, index) => (
            <Box
              key={review.id}
              data-testid={`admin-review-card-${review.id}`}
              data-review-status={review.status}
              sx={{ position: 'relative' }}
            >
              <ReviewCardComponent
                review={review}
                index={index}
                userId={user?.id}
                filterType={'date' as ReviewSearchType}
                showTypeIndicator={false}
                onGoToReview={() => handleGoToReview(review)}
                onGotoCompany={() => {
                  if (review.companyName) {
                    navigate(`/company/${encodeURIComponent(review.companyName)}`);
                  }
                }}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          <Typography variant="body1">No reviews found.</Typography>
        </Alert>
      )}
    </Page>
  );
}

export default AdminReviewFeedPage;
